// src/pages/Main.js - 로직만 포함 (스타일은 Main.styles.js로 분리)

import React, { useState, useEffect, useMemo, useCallback, Suspense, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signInWithCustomToken } from 'firebase/auth'; // [Admin] Firebase Auth
import { doc, updateDoc, increment } from 'firebase/firestore'; // [추가] Firestore 함수
import { db, auth } from '../firebase'; // [Admin] Firebase auth instance
import { gameData, getRandomStagesFromTheme, getThemeConfigByEnvironment, THEMES_PER_PAGE, SUBSCRIPTION_TYPES, getAdWatchPpPoints } from '../config';
import { getConfigNumber } from '../services/remoteConfig';
import { useAppContext } from '../contexts/AppContext';
import { useThemeDownloadContext } from '../contexts/ThemeDownloadContext';
import { useGamePlayContext } from '../contexts/GamePlayContext';
import useThemeDownload from '../hooks/useThemeDownload';
import ThemePagination from '../components/ThemePagination';
// ShopModal & TestAdModal removed

import {
  isPiBrowserEnvironment,
  isSafeEnv,
  debugLog,
  getEnvironment,
  getDisplayEnvironment,
  isThemePlayable,
  maskSensitiveData,
  errorLog // [추가]
} from '../utils/environment';
import usePiAds from '../hooks/usePiAds';
// [수정] 조건부 로깅 함수 추가
import { getAPIUrls } from '../services/api';
import { getConfigString } from '../services/remoteConfig';
import bgmManager from '../services/bgmManager';
import imageLoader from '../services/imageLoader'; // [추가] imageLoader import
import LazyImage from '../components/LazyImage'; // [추가] 지연 로딩 컴포넌트
import AdDebugPanel from '../components/AdDebugPanel'; // [모바일 디버깅] URL ?debug=KEY 시 광고 에러 화면 표시
import performanceMonitor from '../services/performanceMonitor'; // [추가] 성능 모니터링
import { checkAttendanceStatus, isAttendanceClaimedLocally } from '../services/eventService.js'; // [NEW] 출석 상태 확인
import SpotGoMain from './spot/SpotGoMain'; // [New] Spot Go Main Feed
import { SpotProvider } from '../contexts/SpotContext'; // [NEW] Global Spot Subscription
// Analytics 제거됨 - Firebase Analytics 사용 안함
import { validateAdminPassword } from '../services/api'; // [신규 추가]
import {
  IoMenuOutline,
  IoFlashOutline,
  IoLocationOutline,
  IoGridOutline,
  IoPricetagOutline,
  IoPersonOutline
} from 'react-icons/io5';
import * as S from './Main.styles';
const AppsView = lazy(() => import('../components/AppsView'));
const MyView = lazy(() => import('../components/MyView'));
const DealView = lazy(() => import('../components/DealView'));
const ShopView = lazy(() => import('../components/ShopView'));

// 🚀 [성능 최적화] 지연 로딩 컴포넌트들 - 초기 번들 크기 감소
// HallOfFame removed
// DonationModal removed
const HamburgerMenu = lazy(() => import('../components/HamburgerMenu')); // [추가] 햄버거 메뉴
const AdminPasswordModal = lazy(() => import('../components/AdminPasswordModal')); // [신규 추가]
const UsageStatsPanel = lazy(() => import('../components/UsageStatsPanel')); // [신규 추가] 
const MaintenancePanel = lazy(() => import('../components/MaintenancePanel')); // [신규 추가]
const TestPiSender = lazy(() => import('../components/TestPiSender')); // [신규 추가] A2U Test-Pi 전송
const TestPiBonus = lazy(() => import('../components/TestPiBonus'));
const Ranking = lazy(() => import('../components/Ranking')); // 랭킹 컴포넌트 추가
// SeasonRankingModal removed
const AdminDashboard = lazy(() => import('../components/admin/AdminDashboard')); // [Admin] 관리자 대시보드
const VoteCarousel = lazy(() => import('../components/vote/VoteCarousel')); // [NEW] 투표 카루셀
const BannerCarousel = lazy(() => import('../components/BannerCarousel')); // [NEW] 배너 카루셀
const AttendanceModal = lazy(() => import('../components/event/AttendanceModal.js')); // [NEW] 출석체크 모달
const MiniGameModal = lazy(() => import('../components/MiniGameModal')); // [NEW] 미니게임 모달 (가위바위보)



// --- 테마 카드 컴포넌트 ---
const ThemeCardWithDownload = React.memo(({ theme, state, onClick, isThemeDownloaded, isSelected, afterFirstPaint, isFirstCard = false, isSecondCard = false }) => {  // ✅ props에 isSelected, isFirstCard, isSecondCard 추가
  const { t } = useTranslation();
  const {
    downloadStatus,
    downloadProgress,
    startDownload
  } = useThemeDownload(theme.id, theme.startStageId, theme.endStageId);

  // [최적화] 썸네일 URL 상태 관리 - Cloudflare 무료 리사이징 활용
  const getOptimizedThumbnailUrl = (stageId, isFirstCard = false, isSecondCard = false) => {
    const screenWidth = window.innerWidth;
    let width, quality;

    // [LCP 최적화] 네트워크 속도 기반 적응형 로딩 - 안전한 폴백 처리
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const isSlowNetwork = connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g');

    // [안전성] Connection API 미지원 시 보수적 접근 (작은 이미지/낮은 품질)
    const isConnectionSupported = connection && connection.effectiveType;

    if (isFirstCard) {
      // 첫 번째 카드: LCP 후보로 고품질 (네트워크 속도 무시)
      width = screenWidth < 768 ? 120 : screenWidth < 1024 ? 240 : 320;
      quality = 90;
    } else if (isSecondCard) {
      // 두 번째 카드: 네트워크 속도에 따라 조절
      if (!isConnectionSupported) {
        // Connection API 미지원 시 보수적 접근
        width = screenWidth < 768 ? 120 : 180;
        quality = 75;
      } else if (isSlowNetwork) {
        width = screenWidth < 768 ? 120 : 180; // 느린 네트워크에서는 더 작게
        quality = 75;
      } else {
        width = screenWidth < 768 ? 120 : screenWidth < 1024 ? 240 : 240;
        quality = 85;
      }
    } else {
      // 나머지 카드: 최적화
      if (!isConnectionSupported) {
        // Connection API 미지원 시 보수적 접근
        width = screenWidth < 768 ? 120 : 180;
        quality = 70;
      } else {
        width = screenWidth < 768 ? 120 : screenWidth < 1024 ? 240 : 320;
        quality = 80;
      }
    }

    return `https://assets.spot.painori.com/${stageId}/00.png?width=${width}&format=webp&quality=${quality}`;
  };

  const defaultThumbnailUrl = getOptimizedThumbnailUrl(theme.startStageId, isFirstCard, isSecondCard);
  const [thumbnailUrl, setThumbnailUrl] = useState(defaultThumbnailUrl);
  const [isThumbnailLoading, setIsThumbnailLoading] = useState(true);

  // [추가] 썸네일 로드 useEffect
  useEffect(() => {
    // [최적화] 썸네일은 Cloudflare 리사이징 사용
    const optimizedUrl = getOptimizedThumbnailUrl(theme.startStageId, isFirstCard, isSecondCard);
    setThumbnailUrl(optimizedUrl);

    // [LCP 최적화] 첫 번째 카드는 즉시 로딩 완료 처리
    if (isFirstCard) {
      setIsThumbnailLoading(false);
    } else {
      setIsThumbnailLoading(false);
    }

    // 다운로드된 테마는 나중에 캐시된 URL로 교체 (선택사항)
    if (downloadStatus === 'downloaded') {
      imageLoader.getImageUrl(theme.startStageId, '00.png')
        .then(cachedUrl => {
          // 캐시된 URL이 있으면 사용 (실패해도 직접 URL 유지)
          if (cachedUrl) {
            debugLog('[ThemeCard] 캐시된 썸네일 발견:', theme.id);
            // 필요시 setThumbnailUrl(cachedUrl); 활성화 가능
          }
        })
        .catch(() => {
          // 에러 무시 - 이미 직접 URL 사용 중
        });
    }
  }, [theme.startStageId, downloadStatus, theme.id]);

  const handleClick = () => {
    if (state.isComingSoon) {
      return;
    }

    // [수정] 다운로드 중일 때 메시지 표시
    if (downloadStatus === 'downloading') {
      alert(t('download_in_progress'));
      return;
    }

    if (!state.isLocked && !isThemeDownloaded(theme.id) && downloadStatus === 'not-downloaded') {
      // 무료 테마이지만 다운로드 안됨 -> 다운로드 시작
      startDownload();
      return;
    }

    if (downloadStatus === 'downloaded' || state.isLocked) {
      // 다운로드 완료 또는 잠긴 테마 -> 기존 로직 실행
      onClick(theme);
    }
  };

  const isLocked = state.isLocked;
  const isComingSoon = state.isComingSoon;
  const price = state.price || theme.price;

  // [수정] 썸네일은 항상 표시 (로딩 중이어도 기본 URL 사용)

  // [수정] 디버깅 로그 조건부로 변경 - 최초 렌더링 시에만
  useEffect(() => {
    debugLog('[ThemeCard] 렌더링:', {
      themeId: theme.id,
      downloadStatus: downloadStatus,
      isLocked: isLocked
    });
  }, [theme.id, downloadStatus, isLocked]);

  return (
    <S.ThemeCard
      $bgImage={thumbnailUrl}
      $isLocked={isLocked}
      $isComingSoon={isComingSoon}
      $isDownloading={downloadStatus === 'downloading'}
      $isDownloaded={downloadStatus === 'downloaded'}
      $afterFirstPaint={afterFirstPaint}
      $isSelected={isSelected}
      onClick={handleClick}
    >
      {/* [최적화] 지연 로딩 썸네일 이미지 - 배경 이미지로 사용하므로 숨김 */}
      <LazyImage
        src={thumbnailUrl}
        alt={theme.name}
        className="theme-thumbnail"
        onLoad={() => setIsThumbnailLoading(false)}
        onError={() => {
          debugLog('[ThemeCard] 썸네일 로드 실패, 기본 이미지 사용');
          setThumbnailUrl(defaultThumbnailUrl);
          setIsThumbnailLoading(false);
        }}
        style={{ display: 'none' }}
        // [LCP 최적화] 첫 번째 카드만 LCP 후보, 두 번째는 UX만 빠르게
        fetchPriority={isFirstCard ? 'high' : isSecondCard ? 'auto' : 'low'}
        loading={isFirstCard || isSecondCard ? 'eager' : 'lazy'}
        decoding="async"
      />
      {/* [수정] 썸네일 로딩 중 표시 - 다운로드된 테마에서 캐시 로드 중일 때만 */}
      {isThumbnailLoading && downloadStatus === 'downloaded' && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          fontSize: '1.5rem',
          opacity: 0.5
        }}>
          ⏳
        </div>
      )}

      <S.ThemeOverlay $isLocked={isLocked || isComingSoon}>
        <S.ThemeTitle>{t(theme.nameKey)}</S.ThemeTitle>
        <S.ThemeStatus>
          {isLocked || isComingSoon
            ? t('locked')
            : downloadStatus === 'downloaded'
              ? theme.bestScore ? `${t('best_score')}: ${theme.bestScore.toLocaleString()}` : t('new_theme')
              : ''}
        </S.ThemeStatus>
      </S.ThemeOverlay>

      {/* 선택 상태 표시 */}
      {isSelected && (
        <S.SelectionIndicator>
          <span>✓</span>
        </S.SelectionIndicator>
      )}

      {(isLocked || isComingSoon) && (
        <>
          <S.LockIcon>🔒</S.LockIcon>
          {!isComingSoon && (
            <S.PriceTag>
              <span>{price} {t('pi_unit')}</span>
            </S.PriceTag>
          )}
        </>
      )}

      {!isLocked && !isComingSoon && downloadStatus === 'not-downloaded' && (
        <S.DownloadButton>
          <span>💾 {t('download')}</span>
        </S.DownloadButton>
      )}

      {!isLocked && !isComingSoon && downloadStatus === 'downloading' && (
        <S.DownloadProgress>
          <S.ProgressBar $progress={downloadProgress}>
            <div className="fill" />
          </S.ProgressBar>
          <S.ProgressText>{downloadProgress}%</S.ProgressText>
        </S.DownloadProgress>
      )}
    </S.ThemeCard>
  );
});

// --- 메인 컴포넌트 ---
const Main = ({ piUser }) => {
  const navigate = useNavigate();
  const location = useLocation(); // [NEW] location 추가
  const { t } = useTranslation();
  const [showRankingModal, setShowRankingModal] = useState(false); // 랭킹 모달 상태
  const [rankingActiveTab, setRankingActiveTab] = useState('total'); // 랭킹 모달 활성 탭
  // [Deleted] showSeasonRankingModal
  const [showShopModal, setShowShopModal] = useState(false); // 상점 모달 상태
  const [showMiniGameModal, setShowMiniGameModal] = useState(false); // 미니게임 모달 상태
  const [showShopAdModal, setShowShopAdModal] = useState(false); // [New] Shop Ad Modal State

  // [Deleted] showNoticeModal, hasNewNotice

  // [NEW] 토스트 메시지 상태
  const [toastMessage, setToastMessage] = useState(null);

  // [NEW] location.state로 전달된 토스트 감지
  useEffect(() => {
    if (location.state?.bonusToast) {
      debugLog('[Main] 토스트 메시지 감지:', location.state.bonusToast);
      setToastMessage(location.state.bonusToast);

      // 3초 후 토스트 제거
      const timer = setTimeout(() => {
        setToastMessage(null);
      }, 3000);

      // state 클리어 (새로고침 시 중복 방지)
      const state = { ...window.history.state };
      if (state.usr) {
        delete state.usr.bonusToast;
        window.history.replaceState(state, '');
      }

      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // [NEW] 랭킹전 로딩 상태 (티켓 사용 대기)
  const [isRankingLoading, setIsRankingLoading] = useState(false);

  // 🎯 [수정] 뷰 모드 상태 관리 (기본값: 'spot')
  // 월드투어 등에서 sessionStorage로 APPS 요청 시 라우트 state만으로는 누락될 수 있어 1회 보조
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = sessionStorage.getItem('painori-main-initial-viewMode');
      if (v && ['apps', 'spot', 'my', 'deal', 'shop'].includes(v)) {
        sessionStorage.removeItem('painori-main-initial-viewMode');
        return v;
      }
    } catch (_) { /* ignore */ }
    return 'spot';
  });

  // APPS↔SPOT 전환 시 SpotGoMain을 유지해 GPS/지도 상태를 메인넷과 같이 보존한다
  const [keepSpotMounted, setKeepSpotMounted] = useState(() => viewMode === 'spot');
  useEffect(() => {
    if (viewMode === 'spot') {
      setKeepSpotMounted(true);
      return;
    }
    if (viewMode !== 'apps') {
      setKeepSpotMounted(false);
    }
  }, [viewMode]);

  // 🔧 선택된 테마 상태를 localStorage에서 복원
  const [selectedTheme, setSelectedTheme] = useState(() => {
    const saved = localStorage.getItem('painori-selected-theme');
    return saved ? JSON.parse(saved) : null;
  });

  // 🔧 [NEW] location.state 변경 감지하여 viewMode 업데이트 (동일 경로 내 이동 시)
  useEffect(() => {
    // 🎯 [Fix] location.state.viewMode가 있을 때만 업데이트
    if (location.state?.viewMode) {
      debugLog('[Main] 뷰 모드 변경 요청 감지 (location.state):', location.state.viewMode);
      setViewMode(location.state.viewMode);

      // 🎯 [Fix] 상태 적용 후 history state 클리어하여 재진입/리렌더링 시 간섭 방지
      const state = { ...window.history.state };
      if (state.usr && state.usr.viewMode) {
        delete state.usr.viewMode;
        window.history.replaceState(state, '');
      }
    }
  }, [location.state]);

  // 🔧 테마 선택 시 localStorage에 저장하는 함수
  const selectTheme = useCallback((theme) => {
    setSelectedTheme(theme);
    localStorage.setItem('painori-selected-theme', JSON.stringify(theme));
    debugLog('[Main] 테마 선택 및 저장:', theme?.id);
  }, []);
  const {
    isSoundEnabled,
    toggleSound,
    currentLanguage,
    changeLanguage,
    totalHonorScore,
    getCurrentTier,
    processPiPayment,
    isThemePurchased,
    isProcessingPayment,
    purchasedThemes,
    environment,
    totalClearCount,
    currentThemePage,
    setCurrentThemePage,
    themeHighScores, // [추가] 테마별 최고 점수 상태
    // 개발자 리셋 함수들
    resetPurchasedThemes,
    resetHonorScore,
    clearThemeProgress,
    resetAllGameData,
    resetGameStats,
    clearImageCache,
    // 구독 관련
    subscriptionStatus,
    processPiPaymentSubscription,
    hasActiveSubscription,
    getActiveSubscriptionBenefits,
    checkSubscriptionStatus,
    resetSubscriptionStatus,
    isGuest,
    initialSyncError,
    // 🆕 [2025-12-19 추가] 테스트 모드 관련
    isTestMode,
    canMakePayment,
    ticketCount,
    setTicketCount,
    processTicketPurchase,
    watchAdForTicket,
    watchAdForPpPoints,
    // [추가] PP 포인트 시스템
    ppPoints,
    addPpPoints, // [추가] 포인트 획득 함수
    spendPpPoints,
    recoverPpPoints, // [추가] STAR 포인트 복구 함수
    // 🎯 [추가] 진동 관련
    isVibrationEnabled,
    toggleVibration,
    claimAttendanceReward, // 🆕 [2026-01-29] 출석 체크 보상
    restoreGameState, // [추가] 게임 상태 복원 함수
    setHeavyTaskState // 🎯 [AppMode] 전역 리소스 관리 (광고 시청 시)
  } = useAppContext();

  // 🎯 [V2] Pi Ads Hook (lastAdDebugInfo: URL ?debug=KEY 시 모바일에서 광고 에러 원인 확인용)
  const { showAd, isLoading: isAdLoading, lastAdDebugInfo, clearLastAdDebugInfo } = usePiAds();

  // 🎯 [AppMode] 광고 로딩 상태를 전역 리소스 관리 모드로 전파
  // isAdLoading이 true면 지도 등을 일시 중지하여 리소스 확보
  useEffect(() => {
    setHeavyTaskState(isAdLoading, 'ad_loading_main');

    // 🛡️ [Safety] 컴포넌트 언마운트 시 강제로 모드 초기화
    // (예: 광고 중 뒤로가기 등으로 페이지 이탈 시)
    return () => {
      setHeavyTaskState(false, 'ad_loading_main');
    };
  }, [isAdLoading, setHeavyTaskState]);

  const {
    isThemeDownloaded
  } = useThemeDownloadContext();

  const {
    startNewGame
  } = useGamePlayContext();

  // [수정] 환경 확인 - AppContext의 environment 사용
  const isSandbox = environment === 'sandbox' || environment === 'testnet';
  const displayEnv = getDisplayEnvironment(); // 'testnet' 또는 'mainnet'

  // 🎯 [추가] 구매 완료 후 강제 리렌더링 트리거
  const [purchaseUpdateTrigger, setPurchaseUpdateTrigger] = useState(0);

  // 🎯 [단순화] 이벤트 리스너 제거 - AppContext 상태 변경으로 자동 리렌더링

  // [추가] 게임 상태 복원 로직
  useEffect(() => {
    const restoredState = restoreGameState();
    if (restoredState) {
      debugLog('[Main] 게임 상태 복원됨:', maskSensitiveData(restoredState));
      // 복원된 상태에 따른 추가 처리 로직
      // 예: 특정 스테이지로 이동, 점수 복원 등
    }
  }, [restoreGameState]);
  // React의 기본 상태 관리 활용으로 복잡한 이벤트 시스템 불필요

  // 🆕 티켓 롤백 이벤트 리스너
  useEffect(() => {
    const handleTicketRollback = (event) => {
      debugLog('[Main] 티켓 롤백 이벤트 수신:', event.detail);

      const { originalTicketCount, reason } = event.detail;

      // AppContext 상태 업데이트
      setTicketCount(originalTicketCount);

      // UI 업데이트 트리거
      setPurchaseUpdateTrigger(prev => prev + 1);

      debugLog('[Main] 티켓 롤백 완료:', {
        reason,
        restoredTicketCount: originalTicketCount
      });
    };

    window.addEventListener('ticketRollback', handleTicketRollback);
    return () => {
      window.removeEventListener('ticketRollback', handleTicketRollback);
    };
  }, [setTicketCount]);

  // 🔴 [REMOVED 2026-01-29] 시즌 랭킹 이벤트 리스너 제거

  // 🎯 [추가] 구매 완료 이벤트 리스너 - UI 즉시 반영
  useEffect(() => {
    const handlePurchaseCompleted = (event) => {
      debugLog('[Main] 구매 완료 이벤트 수신:', event.detail);
      // 강제 리렌더링 트리거
      setPurchaseUpdateTrigger(prev => prev + 1);
    };

    window.addEventListener('purchaseCompleted', handlePurchaseCompleted);
    return () => {
      window.removeEventListener('purchaseCompleted', handlePurchaseCompleted);
    };
  }, []);

  // [추가] STAR 포인트 동기화 감지
  useEffect(() => {
    const handleStarPointsSync = (event) => {
      debugLog('[Main] STAR 포인트 동기화 감지:', event.detail);
      // 강제 리렌더링을 위한 상태 업데이트
      setPurchaseUpdateTrigger(prev => prev + 1);
    };

    window.addEventListener('starPointsSynced', handleStarPointsSync);

    return () => {
      window.removeEventListener('starPointsSynced', handleStarPointsSync);
    };
  }, []);

  // 🆕 [2025-12-19 추가] 기능 제어 상태
  // 🔴 [REMOVED 2026-01-29] 시즌 랭킹 관련 설정 제거
  const isShopEnabled = isTestMode || getConfigString('shop_enabled') === 'true';
  const isNftEnabled = isTestMode || getConfigString('nft_enabled') === 'true';
  const isMenuEnabled = isTestMode || getConfigString('menu_enabled') === 'true';
  const isMiniGameEnabled = isTestMode || getConfigString('minigame_enabled') === 'true';

  // 🔴 [REMOVED 2026-01-29] 시즌 시간 업데이트 로직 제거

  // 🔴 [REMOVED 2026-01-29] 시즌 표시 정보 계산 제거

  // 버전 정보 간소화 (사용하지 않는 변수 제거)
  // const appVersion = (typeof process !== 'undefined' && process.env ? process.env.REACT_APP_VERSION : null) || '1.0.0';
  // const buildDate = (typeof process !== 'undefined' && process.env ? process.env.REACT_APP_BUILD_DATE : null) || new Date().toISOString().split('T')[0];
  // const shortDate = buildDate.split('-').slice(1).join('/'); // MM/DD 형식

  // [수정] 환경별 테마 설정 가져오기 - useMemo로 최적화
  const themeConfig = useMemo(() => getThemeConfigByEnvironment(environment), [environment]);

  // 시스템 도구 3단계 액세스 관련 state
  const [tapCount, setTapCount] = useState(0);
  const [lastTapTime, setLastTapTime] = useState(0);

  // 🔧 [캐시 수정] 개발자 도구 상태를 localStorage에 저장하여 새로고침/탭 전환 시에도 유지
  const [nicknameClicks, setNicknameClicks] = useState(() => {
    try {
      const saved = localStorage.getItem('dev-tools-nickname-clicks');
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });

  const [lastNicknameClickTime, setLastNicknameClickTime] = useState(() => {
    try {
      const saved = localStorage.getItem('dev-tools-last-nickname-click');
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });

  const [showDevTools, setShowDevTools] = useState(false);

  // 광고 제거 모달 상태 (Removed)
  // 후원 모달 상태 (Removed)

  // [신규 추가] 시스템 도구 관리 상태
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUsagePanel, setShowUsagePanel] = useState(false);
  const [showMaintenancePanel, setShowMaintenancePanel] = useState(false);
  // 관리자 도구는 첫 사용 시 내려받고, 닫은 뒤에도 상태와 조회 캐시를 유지한다.
  const [usagePanelMounted, setUsagePanelMounted] = useState(false);
  const [maintenancePanelMounted, setMaintenancePanelMounted] = useState(false);
  const [showAnonymousUserManager, setShowAnonymousUserManager] = useState(false);
  const [showTestPiSender, setShowTestPiSender] = useState(false);
  const [systemAuthValue, setSystemAuthValue] = useState('');
  const [isValidatingAuth, setIsValidatingAuth] = useState(false);

  // [Admin] 관리자 모드 상태 - ✅ AdminPage로 이동됨 (레거시 코드 유지)
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showAdminPasswordModal, setShowAdminPasswordModal] = useState(false);

  // 🎯 [추가] 햄버거 메뉴 상태
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);

  // 🚀 [성능 최적화] 첫 페인트 후 애니메이션 활성화 상태
  const [afterFirstPaint, setAfterFirstPaint] = useState(false);

  // [New] Attendance State — Apps에서 직접 열 때만 표시. 첫 실행 자동 팝업은 사용하지 않음
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceClaimed, setAttendanceClaimed] = useState(false);

  useEffect(() => {
    const claimed = !!(piUser?.uid && !isGuest() && isAttendanceClaimedLocally(piUser.uid, environment));
    setAttendanceClaimed(claimed);
  }, [piUser?.uid, environment, isGuest]);

  const openAttendance = useCallback(async () => {
    if (piUser?.uid && !isGuest()) {
      try {
        const data = await checkAttendanceStatus(piUser.uid, environment);
        if (data) {
          setAttendanceData(data);
          if (data.todayClaimed) setAttendanceClaimed(true);
        }
      } catch (err) {
        debugLog('Attendance check failed', err);
      }
    }
    setShowAttendanceModal(true);
  }, [piUser, environment, isGuest]);

  const closeAttendance = useCallback(() => {
    setShowAttendanceModal(false);
    if (piUser?.uid && !isGuest() && isAttendanceClaimedLocally(piUser.uid, environment)) {
      setAttendanceClaimed(true);
    }
  }, [piUser, environment, isGuest]);


  // 🎯 [수정] 프리로딩 관련 상태 제거 - 백그라운드 처리로 변경
  // const [isPreloading, setIsPreloading] = useState(false);
  // const [preloadingMessage, setPreloadingMessage] = useState('');

  // [수정] 게스트 사용자 닉네임 처리
  // 🎯 [간소화] piUser가 없으면 게스트로 처리
  const isGuestUser = !piUser || sessionStorage.getItem('user-type') === 'guest';
  const userNickname = isGuestUser
    ? 'GUEST'  // 🎯 [수정] 게스트 표시를 GUEST로 변경
    : (piUser?.username || sessionStorage.getItem('pi-username') || 'Pi User');
  const userId = piUser?.uid || sessionStorage.getItem('pi-user-id');

  // useMemo로 isThemePlayable 메모이제이션
  const isThemePlayable = useMemo(() => {
    return (theme) => {
      const config = themeConfig[theme.id];
      if (config?.isComingSoon) return false;
      return (!config?.isLocked || isThemePurchased(theme.id)) && isThemeDownloaded(theme.id);
    };
  }, [isThemePurchased, themeConfig, isThemeDownloaded]);

  // 프리미엄 사용자는 힌트 +1
  const [userHearts] = useState(5);
  const currentTier = getCurrentTier();
  const subscriptionBenefits = getActiveSubscriptionBenefits();
  const isPremium = subscriptionStatus?.type === '30d' && hasActiveSubscription();

  // eslint-disable-next-line no-unused-vars
  const [themeProgress, setThemeProgress] = useState({});

  // 페이지네이션 계산
  const totalPages = Math.ceil(gameData.themes.length / THEMES_PER_PAGE);
  const currentThemes = gameData.themes.slice(
    currentThemePage * THEMES_PER_PAGE,
    (currentThemePage + 1) * THEMES_PER_PAGE
  );

  // 🎯 [추가] 기본 선택 테마 설정 (첫 번째 다운로드된 테마)
  useEffect(() => {
    if (!selectedTheme && currentThemes.length > 0) {
      const firstDownloadedTheme = currentThemes.find(theme => isThemeDownloaded(theme.id));
      if (firstDownloadedTheme) {
        selectTheme(firstDownloadedTheme);
      }
    }
  }, [currentThemes, selectedTheme, isThemeDownloaded, selectTheme]);

  // 🔧 [핵심 수정] 1단계: 닉네임 3회 클릭 핸들러 - 환경별 DEV_ID 체크
  const handleNicknameClick = useCallback(() => {
    const isPiUser = !isGuest() && piUser?.uid;
    if (!isPiUser) {
      return;  // Pi 로그인 체크
    }

    // 🔧 [보안 강화] .env.local 기반 개발자 ID 확인
    // Create React App에서 process.env는 빌드 시점에 주입되므로 런타임에서 직접 접근 가능
    const expectedDevId = environment === 'production'
      ? process.env.REACT_APP_MAINNET_DEV_ID  // 메인넷 환경
      : process.env.REACT_APP_DEV_ID;         // 테스트넷/샌드박스 환경

    // 🔧 [디버그] 환경 변수 로드 상태 확인
    if (hasLogAccess()) {
      debugLog('[Main] 개발자 ID 로드 상태:', {
        environment,
        expectedDevId: expectedDevId ? maskSensitiveData({ id: expectedDevId }).id : 'undefined',
        actualUserId: maskSensitiveData({ id: piUser?.uid }).id,
        processEnvAvailable: typeof process !== 'undefined' && process.env
      });
    }

    // 🔒 [보안 강화] 개발자 ID 검증 (디버그 모드에서만 로그)
    if (piUser?.uid !== expectedDevId) {
      if (hasLogAccess()) {
        debugLog('[Main] 개발자 ID 불일치:', {
          environment,
          expectedDevId: maskSensitiveData({ id: expectedDevId }).id,
          actualUserId: maskSensitiveData({ id: piUser?.uid }).id
        });
      }
      return;  // 개발자가 아닌 경우 종료
    }

    const currentTime = Date.now();
    if (currentTime - lastNicknameClickTime > 2000) {
      const newCount = 1;
      setNicknameClicks(newCount);
      // 🔧 [캐시 수정] localStorage에 상태 저장
      try {
        localStorage.setItem('dev-tools-nickname-clicks', newCount.toString());
        localStorage.setItem('dev-tools-last-nickname-click', currentTime.toString());
      } catch (error) {
        debugLog('[Main] 개발자 도구 상태 저장 실패:', error);
      }
      if (hasLogAccess()) {
        debugLog('[Main] 닉네임 클릭 카운트 리셋:', { newCount: 1, timeDiff: currentTime - lastNicknameClickTime });
      }
    } else {
      const newCount = nicknameClicks + 1;
      setNicknameClicks(newCount);
      // 🔧 [캐시 수정] localStorage에 상태 저장
      try {
        localStorage.setItem('dev-tools-nickname-clicks', newCount.toString());
        localStorage.setItem('dev-tools-last-nickname-click', currentTime.toString());
      } catch (error) {
        debugLog('[Main] 개발자 도구 상태 저장 실패:', error);
      }
      if (hasLogAccess()) {
        debugLog('[Main] 닉네임 클릭 카운트 증가:', {
          previousCount: nicknameClicks,
          newCount,
          timeDiff: currentTime - lastNicknameClickTime
        });
      }
    }
    setLastNicknameClickTime(currentTime);
  }, [isGuest, piUser, lastNicknameClickTime, nicknameClicks, environment]);

  /** 개발자 통계 알림 — preloadManager는 이 시점에만 동적 로드 (메인 진입 시 모듈 부담 제거) */
  const showGameStatsWithPreload = useCallback(async () => {
    let preloadSection = '';
    try {
      const { default: preloadManager } = await import('../services/preloadManager');
      const stats = preloadManager.getStats();
      preloadSection =
        `\n\n📡 프리로드 통계\n` +
        `예측 정확도: ${stats.accuracy}\n` +
        `프리로드된 스테이지: ${stats.preloadedCount}개\n` +
        `평균 로드 시간: ${stats.averageLoadTime}\n` +
        `네트워크 속도: ${stats.networkSpeed}\n` +
        `캐시 히트율: ${stats.cacheStats.hitRate}\n`;
    } catch (err) {
      errorLog('[Main] 프리로드 통계 로드 실패:', err);
      preloadSection = '\n\n(프리로드 통계를 불러오지 못했습니다)';
    }
    alert(
      `📊 게임 통계\n` +
      `총 명예 점수: ${totalHonorScore.toLocaleString()}\n` +
      `현재 등급: ${currentTier.name} (${t(currentTier.nameKey)})\n` +
      `총 클리어 횟수: ${totalClearCount}\n` +
      `구매한 테마: ${Object.keys(purchasedThemes).length}개` +
      preloadSection
    );
  }, [t, totalHonorScore, currentTier, totalClearCount, purchasedThemes]);

  // 2단계: 제목 7회 탭 핸들러 (Pi 로그인 + 닉네임 3회 클릭 확인)
  const handleTitleTap = useCallback(() => {
    // 🔧 [디버그] 함수 호출 확인
    debugLog('[Main] handleTitleTap 호출됨:', {
      nicknameClicks,
      tapCount,
      isPiUser: !isGuest() && piUser?.uid
    });

    const isPiUser = !isGuest() && piUser?.uid;
    if (!isPiUser) {
      return;  // Pi 로그인 체크 추가
    }

    // 닉네임 3회 클릭 완료 확인
    if (nicknameClicks !== 3) {
      return;
    }

    const currentTime = Date.now();

    // 2초 이내에 탭하지 않으면 카운트 리셋
    if (currentTime - lastTapTime > 2000) {
      setTapCount(1);
    } else {
      const newCount = tapCount + 1;
      setTapCount(newCount);

      // 7번 탭하면 인증 모달 표시
      if (newCount >= 7) {
        setShowPasswordModal(true);
        setTapCount(0);
        setNicknameClicks(0);  // 초기화

        void showGameStatsWithPreload();
      }
    }

    setLastTapTime(currentTime);
  }, [nicknameClicks, tapCount, lastTapTime, isGuest, piUser, showGameStatsWithPreload]);

  // 3단계: 시스템 인증 함수 (Remote Config 연동)
  const handleAuthSubmit = useCallback(async (authValue) => {
    if (!authValue.trim()) return;

    setIsValidatingAuth(true);
    try {
      // Remote Config에서 시스템 인증키 가져오기
      let systemAuthFromConfig;
      try {
        systemAuthFromConfig = getConfigString('system_auth_key');
        if (!systemAuthFromConfig) {
          throw new Error('System configuration not available');
        }
      } catch (error) {
        // Remote Config 실패 시 API 방식 사용
        errorLog('[Main] Configuration access failed, using API validation:', error);
        const userId = piUser?.uid || sessionStorage.getItem('pi-user-id');
        await validateAdminPassword(authValue, userId, environment);

        setShowPasswordModal(false);
        setShowDevTools(true);
        setSystemAuthValue(authValue);

        await showGameStatsWithPreload();
        return;
      }

      // Remote Config 인증키와 입력값 비교
      if (authValue === systemAuthFromConfig) {
        setShowPasswordModal(false);
        setShowDevTools(true);
        setSystemAuthValue(authValue);

        await showGameStatsWithPreload();
      } else {
        throw new Error('Authentication failed');
      }
    } catch (error) {
      console.error('[Main] System authentication failed:', error);
      alert('Authentication failed. Please check your credentials.');
    } finally {
      setIsValidatingAuth(false);
    }
  }, [piUser, t, totalHonorScore, currentTier, totalClearCount, purchasedThemes, environment, showGameStatsWithPreload]);

  // [신규 추가] 인증 모달 취소 함수
  const handleAuthCancel = useCallback(() => {
    setShowPasswordModal(false);
    setSystemAuthValue('');
    setIsValidatingAuth(false);
    setNicknameClicks(0);  // 초기화
    setTapCount(0);        // 초기화
  }, []);

  // [추가] URL 파라미터나 location state로 뷰 모드 변경
  useEffect(() => {
    // 1. location.state.viewMode 확인 (Game에서 넘어올 때)
    if (location.state?.viewMode) {
      debugLog('[Main] 뷰 모드 변경 요청 (State):', location.state.viewMode);
      setViewMode(location.state.viewMode);

      // state 소비 후 초기화 (선택적)
      // history.replaceState({}, document.title); 
    }
  }, [location.state]);

  // ✅ [Admin] App.js로 이동됨 - 이 코드는 더 이상 실행되지 않음 (레거시 유지)
  // useEffect(() => {
  //   const searchParams = new URLSearchParams(location.search);
  //   const modeParam = searchParams.get('mode');
  //   ...
  // }, [location.search]);

  // [Admin] 관리자 인증 핸들러 (비밀번호 검증만 - 서비스 계정 없이 동작)
  const handleAdminAuthSubmit = useCallback(async (password) => {
    setIsValidatingAuth(true);
    try {
      debugLog('[Admin] 관리자 인증 시도...');

      // validateAdminPassword API 호출
      const result = await validateAdminPassword(password, null, environment);

      if (result.success) {
        debugLog('[Admin] 비밀번호 검증 성공!');
        setSystemAuthValue(password); // [수정] 비밀번호 상태 저장

        // 토큰이 있으면 Firebase Auth 로그인 시도 (옵션)
        if (result.token) {
          try {
            await signInWithCustomToken(auth, result.token);
            debugLog('[Admin] Firebase 관리자 로그인 성공!');
          } catch (tokenError) {
            // 토큰 로그인 실패해도 대시보드는 표시 (비밀번호 인증은 통과)
            console.warn('[Admin] Firebase 토큰 로그인 실패 (대시보드 접근은 허용):', tokenError.message);
          }
        }

        setShowAdminPasswordModal(false);
        setShowAdminDashboard(true);
      } else {
        throw new Error('비밀번호가 일치하지 않습니다.');
      }
    } catch (error) {
      console.error('[Admin] 인증 실패:', error);
      alert('인증에 실패했습니다. 비밀번호를 확인해주세요.');
    } finally {
      setIsValidatingAuth(false);
    }
  }, [environment]);

  // [Admin] 관리자 모달 취소 핸들러
  const handleAdminAuthCancel = useCallback(() => {
    setShowAdminPasswordModal(false);
    setIsAdminMode(false);
    // URL에서 ?mode=admin 제거
    const newUrl = window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, []);

  // [Admin] 대시보드 닫기 핸들러
  const handleAdminDashboardClose = useCallback(() => {
    setShowAdminDashboard(false);
    setIsAdminMode(false);
    // URL에서 ?mode=admin 제거
    const newUrl = window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, []);

  // [최적화] 리셋 핸들러들 - 사용하지 않는 함수들 제거

  const handleResetImages = useCallback(async () => {
    if (window.confirm(t('reset_images_confirm'))) {
      await clearImageCache();
      setShowDevTools(false);
      alert(t('images_reset_complete'));
    }
  }, [t, clearImageCache]);

  const handleResetAll = useCallback(async () => {
    if (window.confirm(t('reset_all_confirm'))) {
      await resetAllGameData();
      setShowDevTools(false);
      alert(t('all_data_reset_complete'));
    }
  }, [t, resetAllGameData]);

  // [최적화] 구독 리셋 핸들러 - 사용하지 않는 함수들 제거

  // 🔧 [신규] 익명 사용자 관리 함수들
  const [anonymousUserStats, setAnonymousUserStats] = useState(null);
  const [isDeletingAnonymous, setIsDeletingAnonymous] = useState(false);

  const fetchAnonymousUserStats = useCallback(async () => {
    console.log('🔍 [디버그] fetchAnonymousUserStats 함수 호출됨');
    console.log('🔍 [디버그] systemAuthValue:', systemAuthValue);
    console.log('🔍 [디버그] systemAuthValue 타입:', typeof systemAuthValue);
    console.log('🔍 [디버그] systemAuthValue 길이:', systemAuthValue ? systemAuthValue.length : 0);

    if (!systemAuthValue) {
      console.log('❌ [디버그] systemAuthValue가 없음 - 개발자 도구에 접근하지 않음');
      alert(t('dev_tools_required'));
      return;
    }

    try {
      console.log('🔍 [디버그] 익명 사용자 통계 조회 시도', {
        systemAuthValue: systemAuthValue ? `${systemAuthValue.substring(0, 3)}***` : 'null',
        passwordLength: systemAuthValue ? systemAuthValue.length : 0
      });

      const apiUrls = getAPIUrls();
      const response = await fetch(apiUrls.getAnonymousUserStats, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          adminPassword: systemAuthValue
        })
      });

      console.log('🔍 [디버그] 응답 상태:', response.status);
      console.log('🔍 [디버그] 응답 헤더:', response.headers);

      const data = await response.json();
      console.log('🔍 [디버그] 응답 데이터:', data);

      if (data.success) {
        console.log('✅ [디버그] 통계 조회 성공');
        setAnonymousUserStats(data.stats);
      } else {
        console.log('❌ [디버그] 통계 조회 실패:', data.error);
        alert('통계 조회 실패: ' + data.error);
      }
    } catch (error) {
      console.error('❌ [디버그] 익명 사용자 통계 조회 실패:', error);
      console.error('❌ [디버그] 에러 상세:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      alert('통계 조회 중 오류가 발생했습니다.');
    }
  }, [systemAuthValue]);

  const deleteAnonymousUsers = useCallback(async () => {
    if (!systemAuthValue) {
      alert('개발자 도구에 먼저 접근해주세요.');
      return;
    }

    if (!window.confirm('정말로 모든 익명 사용자를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return;
    }

    try {
      setIsDeletingAnonymous(true);

      const apiUrls = getAPIUrls();
      const response = await fetch(apiUrls.deleteAnonymousUsers, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          adminPassword: systemAuthValue,
          batchSize: 1000
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`성공적으로 ${data.totalDeleted}명의 익명 사용자를 삭제했습니다.`);
        setAnonymousUserStats(null); // 통계 초기화
      } else {
        alert('삭제 실패: ' + data.error);
      }
    } catch (error) {
      console.error('익명 사용자 삭제 실패:', error);
      alert('삭제 중 오류가 발생했습니다.');
    } finally {
      setIsDeletingAnonymous(false);
    }
  }, [systemAuthValue]);

  // [통합] 구매/구독 데이터 리셋 핸들러
  const handleResetPurchaseSubscription = useCallback(async () => {
    if (window.confirm('구매한 테마와 구독 정보를 모두 초기화하시겠습니까?')) {
      try {
        resetPurchasedThemes();
        await resetSubscriptionStatus();
        await checkSubscriptionStatus();
        setShowDevTools(false);
        alert('구매/구독 데이터가 초기화되었습니다.');
      } catch (error) {
        alert('초기화 중 오류가 발생했습니다: ' + error.message);
      }
    }
  }, [resetPurchasedThemes, resetSubscriptionStatus, checkSubscriptionStatus]);

  // [통합] 게임 데이터 리셋 핸들러
  const handleResetGameData = useCallback(async () => {
    if (window.confirm('게임 점수, 클리어 횟수, 테마별 최고점수를 모두 초기화하시겠습니까?')) {
      try {
        resetHonorScore();
        clearThemeProgress();
        await resetGameStats();
        setShowDevTools(false);
        alert('게임 데이터가 초기화되었습니다.');
      } catch (error) {
        alert('초기화 중 오류가 발생했습니다: ' + error.message);
      }
    }
  }, [resetHonorScore, clearThemeProgress, resetGameStats]);

  // 구독 남은 시간 계산
  const getTimeLeft = useCallback((expiresAt) => {
    if (!expiresAt) return null;

    const now = new Date().getTime();
    const expires = new Date(expiresAt).getTime();
    const diff = expires - now;

    if (diff <= 0) return null;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days}${t('days_left')} ${hours % 24}${t('hours_left')}`;
    }

    return `${hours}${t('hours_left')} ${minutes}${t('minutes_left')}`;
  }, [t]);



  // [추가] 재시도 핸들러
  const handleRetrySync = useCallback(async () => {
    debugLog('[Main] 동기화 재시도');
    try {
      await checkSubscriptionStatus();
      // 성공하면 자동으로 initialSyncError가 false로 변경됨
    } catch (error) {
      errorLog('[Main] 동기화 재시도 실패:', error);
    }
  }, [checkSubscriptionStatus]);

  // 🔧 게스트 체크 - sessionStorage 기준으로만 판별 (사용하지 않는 변수 제거)

  // 컴포넌트 마운트 시 사용자 데이터 로드
  useEffect(() => {
    const savedProgress = localStorage.getItem('painori-theme-progress');
    if (savedProgress) {
      setThemeProgress(JSON.parse(savedProgress));
    }

    // 🔧 [수정] BGM Manager 즉시 초기화 및 자동 재생
    if (bgmManager) {
      debugLog('[Main] BGM Manager 즉시 초기화');
      bgmManager.userInteracted = true;

      // BGM Manager에 자동 재생 로직이 있으므로 추가 호출 불필요
      debugLog('[Main] BGM Manager 자동 재생 대기 중...');
    }

    // [추가] 사용자 상호작용 감지 (BGM이 이미 재생 중이어도 추가 상호작용 허용)
    const handleUserInteraction = () => {
      if (bgmManager) {
        bgmManager.userInteracted = true;
        // 이미 재생 중이면 추가 재생 시도하지 않음
        if (!bgmManager.isPlaying) {
          bgmManager.playLobbyBGM();
        }
      }
    };

    // 사용자 상호작용 이벤트 리스너 등록
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('touchstart', handleUserInteraction, { once: true });
    document.addEventListener('keydown', handleUserInteraction, { once: true });

    // 🎯 [최적화] 서버 호출 최소화 - AppContext 상태만 사용
    // checkSubscriptionStatus(); // 제거 - AppContext에서 이미 최신 상태 관리
    // recoverPpPoints(); // 제거 - AppContext에서 이미 최신 상태 관리
    // 게임 통계 동기화(syncGameStats)·랭킹 API 프리로드는 AppContext / 랭킹 모달·게임 진입(GameWrapper)에서만 수행

    debugLog('[Main] AppContext 상태 기반 UI 렌더링 - 서버 호출 없음');

    return () => {
      // 로비를 떠날 때는 정지하지 않음
    };
  }, []);

  // 🔧 [제거] 게임 완료 이벤트 기반 해결책 제거 - API 호출 증가 방지
  // 점수 갱신은 AppContext의 syncGameStats에서 서버 우선 정책으로 처리

  // 🚀 [성능 최적화] 첫 페인트 후 애니메이션 활성화
  useEffect(() => {
    // 첫 페인트 후 애니메이션 활성화
    requestIdleCallback(() => {
      setAfterFirstPaint(true);
      debugLog('[Main] 첫 페인트 완료 - 애니메이션 활성화');
    }, { timeout: 1000 }); // 최대 1초 대기
  }, []);

  // 🚀 [성능 최적화] 보조 서비스 지연 로딩 - 첫 페인트 후 실행
  useEffect(() => {
    // 첫 페인트 후 보조 서비스들 지연 로드
    requestIdleCallback(() => {
      debugLog('[Main] 보조 서비스 지연 로딩 시작');

      // Analytics, BGM, PreloadManager 등 백그라운드에서 로드
      // Analytics 제거됨 - Firebase Analytics 사용 안함

      import('../services/bgmManager').then(() => {
        debugLog('[Main] BGM Manager 서비스 로드 완료');
      });
    }, { timeout: 5000 }); // 최대 5초 대기
  }, []);

  // 🚀 [성능 최적화] 성능 모니터링 초기화
  useEffect(() => {
    performanceMonitor.init();

    // 페이지 언로드 시 성능 리포트 생성
    const handleBeforeUnload = () => {
      const report = performanceMonitor.generateReport();
      debugLog('[Main] 성능 모니터링 리포트:', report);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      performanceMonitor.disconnect();
    };
  }, []);

  // [수정] useMemo로 테마 상태 계산 - 의존성 최적화
  const themeStates = useMemo(() => {
    const states = {};
    gameData.themes.forEach(theme => {
      const config = themeConfig[theme.id];
      states[theme.id] = {
        isLocked: config?.isLocked && !isThemePurchased(theme.id),
        isPlayable: isThemePlayable(theme),
        isComingSoon: config?.isComingSoon || false,
        price: config?.price || theme.price
      };
    });
    return states;
  }, [themeConfig, isThemePurchased, isThemePlayable, purchasedThemes, subscriptionStatus, purchaseUpdateTrigger]); // 🎯 [수정] purchaseUpdateTrigger 의존성 추가로 구매 완료 후 즉시 UI 업데이트

  // [최적화] 테마 플레이 시작 함수
  const startThemeMode = useCallback((theme) => {
    debugLog('[Main] 테마 플레이 시작:', theme.id);

    startNewGame('theme', subscriptionStatus);

    const selectedStages = getRandomStagesFromTheme(theme.id);
    debugLog('[Main] 선택된 스테이지:', selectedStages);

    const firstStageId = selectedStages[0];

    const baseHints = 3;
    const bonusHints = subscriptionBenefits?.bonusHints || 0;
    const initialHints = baseHints + bonusHints;

    navigate(`/game/${firstStageId}`, {
      state: {
        stageIdList: selectedStages,
        currentIndex: 0,
        themeId: theme.id,
        themeName: t(theme.nameKey),
        hearts: userHearts,
        playMode: 'theme',
        initialHints: initialHints
      }
    });
  }, [navigate, t, startNewGame, subscriptionBenefits, userHearts, subscriptionStatus]);

  // 🎯 [수정] 테마 클릭 핸들러 - 프리로드를 백그라운드로 처리
  const handleThemeClick = useCallback((theme) => {
    debugLog('[Main] 테마 클릭:', theme.id);
    // Remote Config로 결제 차단 확인

    const config = themeConfig[theme.id];

    if (config?.isComingSoon) {
      debugLog('[Main] Coming Soon 테마 클릭 무시');
      return;
    }

    const isLocked = config?.isLocked && !isThemePurchased(theme.id);

    if (isLocked) {
      // [🚀 새로운 Analytics] 구매 의도 이벤트 - 구매 모달 표시
      // Analytics 제거됨 - Firebase Analytics 사용 안함

      if (!canMakePayment()) {
        alert(t('payment_disabled_message'));
        // [🚀 새로운 Analytics] 결제 불가 상황
        // Analytics 제거됨 - Firebase Analytics 사용 안함
        return;
      }
      // [추가] 게스트 체크
      if (isGuest()) {
        debugLog('[Main] 게스트는 테마 구매 불가');
        alert(t('guest_function_restricted') || '게스트 기능 제한');
        // [🚀 새로운 Analytics] 게스트 구매 시도
        // Analytics 제거됨 - Firebase Analytics 사용 안함
        return;
      }

      debugLog('[Main] 잠긴 테마 - 결제 프로세스 시작');

      const confirmMessage = isSandbox
        ? t('theme_purchase_confirm', {
          themeName: t(theme.nameKey),
          price: config.price
        }) + '\n\n⚠️ ' + t('testnet_purchase_warning')
        : t('theme_purchase_confirm', {
          themeName: t(theme.nameKey),
          price: config.price
        });

      if (window.confirm(confirmMessage)) {
        debugLog('[Main] 사용자가 구매 확인');

        processPiPayment(
          { ...theme, price: config.price },
          (themeId) => {
            debugLog('[Main] 결제 성공 콜백 실행:', themeId);

            // ✅ [2025-09-19 추가] 결제 성공 후 즉시 UI 업데이트를 위한 강제 상태 동기화
            setTimeout(() => {
              debugLog('[Main] 결제 성공 후 UI 강제 업데이트');
              // React의 상태 업데이트가 비동기이므로 약간의 지연 후 처리
            }, 100);

            alert(t('purchase_success', { themeName: t(theme.nameKey) }));

            const playNow = window.confirm(t('play_now_confirm'));
            if (playNow) {
              debugLog('[Main] 바로 플레이 선택');
              startThemeMode(theme);
            }
          },
          (errorCode) => {
            debugLog('[Main] 결제 에러 콜백:', errorCode);

            switch (errorCode) {
              case 'cancelled':
                debugLog('[Main] 사용자가 결제 취소');
                break;

              case 'INSUFFICIENT_BALANCE':
                alert(t('insufficient_balance'));
                break;

              case 'NETWORK_ERROR':
                alert(t('network_error'));
                break;

              case 'Cannot create a payment without "payments" scope':
                alert(t('payment_permission_required'));
                sessionStorage.clear();
                window.location.reload();
                break;

              case 'guest_payment_not_allowed':
                alert(t('guest_function_restricted') || '게스트 기능 제한');
                break;

              default:
                alert(t('purchase_error'));
            }
          }
        );
      } else {
        debugLog('[Main] 사용자가 구매 취소');
        // [🚀 새로운 Analytics] 구매 취소 이벤트
        // Analytics 제거됨 - Firebase Analytics 사용 안함
      }
      return;
    }

    // 🎯 [수정] 테마 선택만 처리
    if (isThemeDownloaded(theme.id)) {
      debugLog('[Main] 테마 선택:', theme.id);
      selectTheme(theme);
    }
  }, [t, themeConfig, isThemePurchased, isSandbox, processPiPayment, isThemeDownloaded, isGuest, canMakePayment, selectTheme]);

  // 🎯 [수정] 테마 플레이 핸들러 - 비활성화 상태 처리 추가
  const handleThemePlay = useCallback(() => {
    if (!selectedTheme) {
      // 테마가 선택되지 않은 경우, 첫 번째 다운로드된 테마를 자동 선택
      const firstDownloadedTheme = currentThemes.find(theme => isThemeDownloaded(theme.id));
      if (firstDownloadedTheme) {
        selectTheme(firstDownloadedTheme);
        debugLog('[Main] 자동 테마 선택:', firstDownloadedTheme.id);
      } else {
        alert(t('select_theme'));
        return;
      }
    }

    const targetTheme = selectedTheme || currentThemes.find(theme => isThemeDownloaded(theme.id));
    if (!targetTheme) {
      alert(t('select_theme'));
      return;
    }

    debugLog('[Main] 테마 플레이 시작:', targetTheme.id);
    // Analytics 제거됨 - Firebase Analytics 사용 안함
    startThemeMode(targetTheme);
  }, [selectedTheme, currentThemes, isThemeDownloaded, startThemeMode, t, selectTheme]);

  // [추가] 상점 모달 핸들러 -> Shop View 전환
  const handleShopClick = () => {
    setViewMode('shop'); // [수정] 모달 대신 뷰 전환
  };

  // [추가] 티켓 구매 핸들러 (PI 결제) - Promise 반환
  const handleTicketPurchase = (ticketAmount, piAmount) => {
    return new Promise((resolve, reject) => {
      processTicketPurchase(
        ticketAmount,
        piAmount,
        (newTicketCount) => {
          alert(t('ticket_purchase_success'));
          debugLog('[Main] 티켓 구매 성공 (PI):', newTicketCount);
          resolve(newTicketCount);
        },
        (errorCode) => {
          switch (errorCode) {
            case 'GUEST_RESTRICTED':
              alert(t('guest_function_restricted'));
              break;
            case 'INSUFFICIENT_BALANCE':
              alert(t('insufficient_pi_balance'));
              break;
            case 'NETWORK_ERROR':
              alert(t('network_error'));
              break;
            default:
              alert(t('ticket_purchase_failed'));
          }
          reject(new Error(errorCode));
        }
      );
    });
  };

  // [수정] PP 포인트로 티켓 구매 핸들러 - 낙관적 업데이트 적용
  const handleTicketPurchaseWithPp = async (ticketAmount, ppAmount) => {
    if (ppPoints < ppAmount) {
      alert(t('pp_insufficient'));
      throw new Error('INSUFFICIENT_PP');
    }

    // 1. 즉시 UI 업데이트 (낙관적 업데이트)
    const currentTickets = parseInt(localStorage.getItem(`painori-ticket-count-${environment}`) || '0');
    const newTicketCount = currentTickets + ticketAmount;
    const newPpAmount = ppPoints - ppAmount;

    // 로컬스토리지 즉시 업데이트
    localStorage.setItem(`painori-ticket-count-${environment}`, newTicketCount.toString());
    localStorage.setItem(`painori-pp-points-${environment}`, newPpAmount.toString());

    // 상태 즉시 업데이트
    setTicketCount(newTicketCount);
    // PP 포인트는 로컬스토리지만 업데이트 (AppContext가 자동으로 동기화)

    // UI 업데이트 트리거
    setPurchaseUpdateTrigger(prev => prev + 1);

    debugLog('[Main] 티켓 구매 즉시 업데이트 (PP):', {
      ticketAmount,
      ppAmount,
      newTicketCount,
      newPpAmount
    });

    // 성공 메시지 즉시 표시
    alert(t('ticket_purchase_success_pp', { count: ticketAmount, amount: ppAmount.toLocaleString() }));

    // 2. 서버 동기화 (백그라운드, 실패 시 롤백)
    try {
      // 🎯 [Server-First] PP 차감 + 티켓 증가 동시 처리 (Cloud Function에서 트랜잭션)
      await spendPpPoints(ppAmount, `ticket_${ticketAmount}`, ticketAmount);

      debugLog('[Main] PP 포인트 + 티켓 서버 동기화 성공');
    } catch (error) {
      errorLog('[Main] PP 포인트 서버 동기화 실패, 롤백:', error);

      // 실패 시 롤백
      localStorage.setItem(`painori-ticket-count-${environment}`, currentTickets.toString());
      localStorage.setItem(`painori-pp-points-${environment}`, ppPoints.toString());
      setTicketCount(currentTickets);
      // PP 포인트는 로컬스토리지만 업데이트 (AppContext가 자동으로 동기화)
      setPurchaseUpdateTrigger(prev => prev + 1);

      alert(t('purchase_failed') || '구매에 실패했습니다. 다시 시도해주세요.');
      throw error; // 에러를 다시 throw하여 상점 UI가 유지되도록 함
    }
  };

  // [수정] 랭킹전 플레이 핸들러 - 🎯 Server-First Strategy & Loading Indicator
  const handleRankingPlayClick = async () => {
    // 🔧 [추가] 게스트 사용자 체크
    if (isGuest()) {
      alert(t('guest_function_restricted'));
      return;
    }

    // 🔴 [REMOVED 2026-01-29] 시즌 랭킹 활성화 체크 제거 (랭킹전은 항상 가능)

    if (ticketCount <= 0) {
      alert(`${t('ranking_no_tickets')}. ${t('ticket_purchase_guide', { shop: t('shop_title') })}`);
      return;
    }

    // 1. 티켓 사용 (Server-First)
    setIsRankingLoading(true);

    try {
      // 🎯 useTicket 호출 (서버 검증 -> 로컬 업데이트)
      // [Fix] consumeTicket으로 호출 (Hook 이름 충돌 방지)
      const success = await consumeTicket(1, 'ranking_play');

      if (!success) {
        // useTicket 내부에서 throw되지 않고 false 반환 시 (사실 useTicket은 throw함)
        throw new Error('TICKET_DEDUCTION_FAILED');
      }

      debugLog('[Main] 티켓 사용 성공 - 랭킹전 진입');

      // 2. 스테이지 생성 및 게임 시작
      // 🔴 [MODIFIED 2026-01-29] 시즌 랭킹 제거로 기본 그림팩만 사용
      const generateRankingStages = () => {
        const targetThemeId = 'masterpiece1'; // 고정값 사용

        // 테마별 스테이지 ID 범위 매핑
        const themeStageRanges = {
          masterpiece1: [10000, 10009],
          masterpiece2: [10010, 10019],
          masterpiece3: [10020, 10029],
          masterpiece4: [10030, 10039],
          masterpiece5: [10040, 10049],
          masterpiece6: [10050, 10059]
        };

        const [startId, endId] = themeStageRanges[targetThemeId];
        const targetStages = gameData.stages.filter(stage =>
          stage.id >= startId && stage.id <= endId
        );

        const shuffledStages = [...targetStages].sort(() => Math.random() - 0.5);
        return shuffledStages.map(stage => stage.id);
      };

      const rankingStageList = generateRankingStages();
      const firstStageId = rankingStageList[0];

      if (firstStageId) {
        // 랭킹전 모드로 게임 시작
        startNewGame('ranking');

        // 이동
        navigate(`/game/${firstStageId}`, {
          state: {
            hearts: 5,
            stageIdList: rankingStageList,
            currentIndex: 0,
            themeId: 'masterpiece_1', // TODO: Dynamic theme name
            themeName: '명화 Ranking',
            playMode: 'ranking',
            initialHints: 0,
            ticketConsumed: true
          }
        });
      }

    } catch (error) {
      errorLog('[Main] 랭킹전 진입 실패 (티켓 사용 오류):', error);
      alert(error.message === 'TICKET_USE_IN_PROGRESS' ? '처리 중입니다...' : t('ticket_sync_failed'));
    } finally {
      setIsRankingLoading(false);
    }
  };

  // [Deleted] handleSeasonRankingClick

  // [추가] 광고 시청으로 티켓 획득 핸들러
  const handleWatchAdForTicket = () => {
    watchAdForTicket(
      (newTicketCount) => {
        alert(t('ticket_obtained', { count: 1 }));
        debugLog('[Main] 광고 시청으로 티켓 획득 성공:', newTicketCount);
      },
      (errorCode) => {
        if (errorCode && String(errorCode).includes('ADS_NOT_SUPPORTED')) {
          const env = getEnvironment();
          const isTestEnv = env === 'sandbox' || env === 'testnet';
          const text = isTestEnv
            ? (t('ad_not_available_testnet') || '테스트 환경에서는 광고를 사용할 수 없습니다. 메인넷에서 이용해 주세요.')
            : (t('ad_update_browser') || 'Pi Browser를 최신 버전으로 업데이트해 주세요.');
          alert(text);
          return;
        }
        switch (errorCode) {
          case 'AD_WATCH_FAILED':
            alert(t('ad_not_ready'));
            break;
          case 'TICKET_AD_COOLDOWN':
            alert(t('ad_cooldown_message'));
            break;
          case 'TICKET_AD_DAILY_LIMIT':
            alert(t('daily_limit_reached'));
            break;
          default:
            alert(t('ticket_purchase_failed'));
        }
      }
    );
  };

  // [추가] 광고 시청으로 PP 포인트 획득 핸들러 (V2 Pi Ads 통합)
  const handleWatchAdForPpPoints = useCallback(() => {
    debugLog('[Main] 광고 시청 요청 (V2)');

    // 1. 광고 제거 구독 활성 상태라면, 광고 모달 없이 즉시 보상 + 쿨다운 처리
    if (hasActiveSubscription && hasActiveSubscription()) {
      debugLog('[Main] 구독 활성 상태 - 광고 없이 STAR 즉시 지급');
      const reward = getAdWatchPpPoints();
      addPpPoints(reward, 'ad_watch_subscription_benefit');
      alert(t('pp_earned', { amount: reward }));

      // 쿨다운 설정
      const lastAdTimeKey = `painori-last-ad-time-${environment}`;
      localStorage.setItem(lastAdTimeKey, new Date().toISOString());
      return;
    }

    // 2. Pi Ads 실행
    showAd('star',
      (result) => {
        // Success Callback
        debugLog('[Main] 광고 시청 성공:', result);
        // 보상 지급은 usePiAds 내부에서 verifyRewardedAd 호출 시 서버/클라이언트 처리됨
        // 다만 UI 업데이트나 알림이 필요할 수 있음
        const reward = getAdWatchPpPoints();
        // usePiAds에서 이미 verifyRewardedAd -> addPpPoints or DB update? 
        // usePiAds logic: verifyRewardedAd returns success. 
        // We probably need to refresh PP. AppContext syncs PP automatically via listener?
        // Let's check verifyRewardedAd in api.js again. It updates Firestore. 
        // AppContext listener will pick it up eventually. 
        // But for better UX, we might want to manually 'refresh' or just alert.
        // Actually, verifyRewardedAd in Cloud Function does the transaction.
        // So we don't need to call addPpPoints again here if the backend did it.
        // Check usePiAds implementation: it calls onSuccess(verifyResult).
        // verifyRewardedAd returns { newBalance, ... }.

        if (result.newBalance) {
          // If manual update needed in context (though listener should handle it)
          // context doesn't expose setPpPoints directly usually, but listener handles it.
          alert(t('pp_earned', { amount: reward }));
        } else {
          // Fallback
          alert(t('pp_earned', { amount: reward }));
        }

        // 쿨다운 설정
        const lastAdTimeKey = `painori-last-ad-time-${environment}`;
        localStorage.setItem(lastAdTimeKey, new Date().toISOString());
      },
      (error) => {
        errorLog('[Main] 광고 시청 실패:', error);
        const msg = error?.message || '';
        // usePiAds에서 이미 안내 메시지 표시한 경우 중복 알림 방지
        if (msg.includes('ADS_NOT_SUPPORTED') || msg.includes('AD_REWARDED_NO_ADID')) return;
        alert(t('ad_load_failed') || '광고를 불러오는데 실패했습니다.');
      }
    );
  }, [hasActiveSubscription, environment, showAd, addPpPoints, t]);

  // [최적화] 랜덤 플레이 핸들러
  const handleRandomPlay = useCallback(() => {
    debugLog('[Main] 랜덤 플레이 시작');
    // Analytics 제거됨 - Firebase Analytics 사용 안함

    const playableThemes = gameData.themes.filter(theme => isThemePlayable(theme));
    debugLog('[Main] 플레이 가능한 테마:', playableThemes.map(t => t.id));

    let allStageIds = [];

    playableThemes.forEach(theme => {
      const stageIds = [];
      for (let i = theme.startStageId; i <= theme.endStageId; i++) {
        stageIds.push(i);
      }
      allStageIds = [...allStageIds, ...stageIds];
    });

    if (allStageIds.length === 0) {
      alert(t('no_unlocked_themes'));
      return;
    }

    const shuffled = [...allStageIds].sort(() => Math.random() - 0.5);
    const selectedStages = shuffled.slice(0, 10);
    debugLog('[Main] 랜덤 플레이 스테이지:', selectedStages);

    startNewGame('random', subscriptionStatus);

    const firstStageId = selectedStages[0];

    const baseHints = 5;
    const bonusHints = subscriptionBenefits?.bonusHints || 0;
    const initialHints = baseHints + bonusHints;

    navigate(`/game/${firstStageId}`, {
      state: {
        stageIdList: selectedStages,
        currentIndex: 0,
        themeName: t('random_play'),
        hearts: userHearts,
        playMode: 'random',
        initialHints: initialHints
      }
    });
  }, [navigate, t, isThemePlayable, startNewGame, subscriptionBenefits, userHearts, subscriptionStatus]);

  // [최적화] 페이지 변경 핸들러
  const handlePageChange = useCallback((pageIndex) => {
    setCurrentThemePage(pageIndex);
  }, [setCurrentThemePage]);

  // [수정] showDevTools 상태 디버깅 - useEffect 내에서만
  useEffect(() => {
    if (showDevTools) {
      debugLog('[Main] showDevTools 상태 변경:', showDevTools);
    }
  }, [showDevTools]);

  // [추가] 초기 동기화 실패 시 차단 화면 표시
  if (initialSyncError && !isGuest()) {
    return (
      <S.MainContainer>
        <S.SyncErrorContainer>
          <S.ErrorIcon>⚠️</S.ErrorIcon>
          <S.ErrorTitle>{t('network_error_title') || '네트워크 연결 오류'}</S.ErrorTitle>
          <S.ErrorMessage>
            {t('network_error_message') || '서버와 연결할 수 없습니다.\n인터넷 연결을 확인하고 다시 시도해주세요.'}
          </S.ErrorMessage>
          <S.RetryButton onClick={handleRetrySync}>
            {t('retry') || '다시 시도'}
          </S.RetryButton>
          <S.BottomBar>
            <S.VoteButton onClick={() => alert(t('community_vote_coming_soon'))}>
              🗳️
            </S.VoteButton>

            <S.LanguageSelectorContainer>
              <S.LanguageSelector value={currentLanguage} onChange={(e) => changeLanguage(e.target.value)}>
                <option value="ko">{t('language_ko')}</option>
                <option value="en">{t('language_en')}</option>
                <option value="es">{t('language_es')}</option>
                <option value="ja">{t('language_ja')}</option>
                <option value="zh-TW">{t('language_zh-TW')}</option>
                <option value="zh">{t('language_zh')}</option>
                <option value="vi">{t('language_vi')}</option>
                <option value="id">{t('language_id')}</option>
                <option value="ms">{t('language_ms')}</option>
                <option value="th">{t('language_th')}</option>
                <option value="fil">{t('language_fil')}</option>
                <option value="hi">{t('language_hi')}</option>
                <option value="bn">{t('language_bn')}</option>
                <option value="ur">{t('language_ur')}</option>
                <option value="de">{t('language_de')}</option>
                <option value="fr">{t('language_fr')}</option>
                <option value="tr">{t('language_tr')}</option>
                <option value="pt">{t('language_pt')}</option>
                <option value="ar">{t('language_ar')}</option>
                <option value="fi">{t('language_fi')}</option>
                <option value="my">{t('language_my')}</option>
                <option value="af">{t('language_af')}</option>
                <option value="ru">{t('language_ru')}</option>
                <option value="fa">{t('language_fa')}</option>
                <option value="uk">{t('language_uk')}</option>
                <option value="pl">{t('language_pl')}</option>
                <option value="it">{t('language_it')}</option>
                <option value="sw">{t('language_sw')}</option>
                <option value="ml">{t('language_ml')}</option>
                <option value="nl">{t('language_nl')}</option>
                <option value="ro">{t('language_ro')}</option>
                <option value="ne">{t('language_ne')}</option>
                <option value="el">{t('language_el')}</option>
              </S.LanguageSelector>
            </S.LanguageSelectorContainer>
          </S.BottomBar>
        </S.SyncErrorContainer>
      </S.MainContainer>
    );
  }

  return (
    <SpotProvider environment={environment} spotTabActive={viewMode === 'spot'}>
      <S.ReducedMotionGlobalStyle />
      {/* 전역 애니메이션 스타일 */}
      <S.GlobalAnimations />

      {/* [NEW] 토스트 메시지 */}
      {toastMessage && (
        <S.StyledToast>
          <span>🎉</span>
          {toastMessage.message}
        </S.StyledToast>
      )}

      {/* 🚀 [LCP 최적화] H1을 초기 렌더 경로로 이동 - SDK/네트워크 대기 없이 즉시 렌더 */}
      <h1 style={{
        position: 'absolute',
        left: '-9999px',
        top: '-9999px',
        fontSize: '2rem',
        fontWeight: 'bold',
        color: '#333',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Noto Sans", "Apple SD Gothic Neo", "Malgun Gothic", sans-serif'
      }}>
        Painori Spot Go - Find hidden objects in famous artworks
      </h1>

      {/* [2025-12-25 수정] 테스트넷 경고 배너 제거됨 - 환경 판단은 유지 */}

      {/* 🆕 [2025-12-19 추가] 테스트 모드 배너 */}
      {isTestMode && (
        <S.TestModeBanner>
          <span className="test-icon">🧪</span>
          <span className="test-text">테스트 모드</span>
        </S.TestModeBanner>
      )}

      <S.MainContainer $noPadding={['spot', 'apps', 'my', 'deal', 'shop'].includes(viewMode)}>
        {/* 🎯 [수정] 뷰 모드에 따른 화면 렌더링 */}
        {(viewMode === 'spot' || keepSpotMounted) && (
          <div
            style={{
              display: viewMode === 'spot' ? 'block' : 'none',
              height: viewMode === 'spot' ? '100%' : 0,
              overflow: 'hidden'
            }}
            aria-hidden={viewMode !== 'spot'}
          >
            <SpotGoMain
              piUser={piUser}
              processPiPayment={processPiPayment}
              userPoints={ppPoints} // passing ppPoints as userPoints
              onMenuClick={() => setShowHamburgerMenu(true)}
              onNavigate={setViewMode}
              spotTabActive={viewMode === 'spot'}
            />
          </div>
        )}
        <Suspense fallback={<div role="status" style={{ padding: '24px', textAlign: 'center' }}>{t('loading')}</div>}>
        {viewMode === 'apps' ? ( // [NEW] Render AppsView
          <AppsView
            onNavigate={setViewMode}
            ppPoints={ppPoints}
            onOpenAttendance={openAttendance}
            attendanceClaimed={attendanceClaimed}
            onShowToast={(msg, type) => setToastMessage({ message: msg, type: type || 'success' })}
            piUser={piUser}
          />
        ) : viewMode === 'my' ? ( // [NEW] Render MyView
          <MyView
            userNickname={userNickname}
            ppPoints={ppPoints}
            onOpenMenu={() => setShowHamburgerMenu(true)}
          />
        ) : viewMode === 'deal' ? ( // [NEW] Render DealView
          <DealView userPoints={ppPoints} />
        ) : viewMode === 'shop' ? ( // [NEW] Render ShopView
          <ShopView
            ppPoints={ppPoints}
            onPurchaseTicket={handleTicketPurchaseWithPp}
            onWatchAdForPp={handleWatchAdForPpPoints}
          />
        ) : (viewMode === 'spot' || keepSpotMounted) ? null : (
          <>
            <S.UserInfoSection>
              <S.WelcomeRow>
                <span>{t('hello')},</span>
                {/* 닉네임에 클릭 이벤트 추가 */}
                <span className="nickname" onClick={handleNicknameClick}>{userNickname}</span>
                <span>{t('nim')}!</span>
                {isPremium && <S.PremiumBadge>⭐ {t('premium')}</S.PremiumBadge>}
                {/* [개선] PP 포인트 표시 */}
                <S.PpPointsDisplay>
                  ⭐ {ppPoints.toLocaleString()} STAR
                </S.PpPointsDisplay>
              </S.WelcomeRow>

              <S.StatsRow>
                <S.StatItem>
                  <span className="label">🎮 {t('lobby_clear_count')}</span>
                  <span className="value">{totalClearCount}</span>
                </S.StatItem>
                <S.StatItem>
                  <span className="label">🏆 {t('lobby_honor_score')}</span>
                  <span className="value">{totalHonorScore.toLocaleString()}</span>
                </S.StatItem>
                <S.StatItem $tierColor={currentTier.color}>
                  <span className="label">🏅 {t('lobby_rank')}</span>
                  <span className="value rank-tier">{t(currentTier.nameKey)}</span>
                </S.StatItem>
                {!isGuest() && (
                  <S.RankingButton onClick={() => {
                    // Analytics 제거됨 - Firebase Analytics 사용 안함
                    setShowRankingModal(true);
                  }}>
                    <span>🏆</span>
                    <span>{t('ranking')}</span>
                  </S.RankingButton>
                )}
              </S.StatsRow>
            </S.UserInfoSection>

            <S.ThemeSection>
              <S.SectionTitleRow>
                <div style={{ flex: 1 }}></div> {/* 좌측 여백을 위한 빈 div */}
                <S.SectionTitle onClick={handleTitleTap}>{t('select_theme')}</S.SectionTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, justifyContent: 'flex-end' }}> {/* 우측 아이템 그룹 */}
                  {/* [삭제됨] 알림 버튼 (NoticeButton) */}
                  {!isGuest() && (environment === 'testnet' || environment === 'sandbox') && (
                    <Suspense fallback={<div style={{ height: '60px', background: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>Loading...</div>}>
                      <TestPiBonus
                        userId={userId}
                        environment={environment}
                        onClaim={(amount) => {
                          console.log(`Claimed ${amount} Test-Pi`);
                        }}
                      />
                    </Suspense>
                  )}
                </div>
              </S.SectionTitleRow>

              {/* 개발자 도구 - 조건부 렌더링 */}
              {showDevTools && (
                <S.DevToolsContainer>
                  {/* 🔧 [신규] 개발자 도구 닫기 버튼 */}
                  <S.DevButton
                    onClick={() => setShowDevTools(false)}
                    style={{
                      backgroundColor: '#ff4757',
                      color: 'white',
                      marginBottom: '10px',
                      width: '100%'
                    }}
                  >
                    ❌ 개발자 도구 닫기
                  </S.DevButton>

                  {/* 모니터링 및 제어 */}
                  <S.DevButton onClick={() => { setUsagePanelMounted(true); setShowUsagePanel(true); }}>
                    📊 사용량 모니터링
                  </S.DevButton>
                  <S.DevButton onClick={() => { setMaintenancePanelMounted(true); setShowMaintenancePanel(true); }}>
                    🔧 서비스 제어
                  </S.DevButton>
                  {/* 테스트넷에서만 TestPiSender 표시 */}
                  {(environment === 'testnet' || environment === 'sandbox') && (
                    <S.DevButton onClick={() => setShowTestPiSender(true)}>
                      💰 Test-Pi 전송
                    </S.DevButton>
                  )}
                  {/* 전체 리셋 */}
                  <S.DevButton onClick={handleResetAll}>
                    🔄 {t('reset_all')}
                  </S.DevButton>

                  {/* 통합된 기능들 */}
                  <S.DevButton onClick={handleResetPurchaseSubscription}>
                    🛒 구매/구독 데이터 리셋
                  </S.DevButton>
                  <S.DevButton onClick={handleResetGameData}>
                    🏆 게임 데이터 리셋
                  </S.DevButton>
                  <S.DevButton onClick={handleResetImages}>
                    🖼️ {t('reset_images')}
                  </S.DevButton>

                  {/* 🔧 [신규] 익명 사용자 관리 */}
                  <S.DevButton onClick={() => setShowAnonymousUserManager(true)}>
                    👤 익명 사용자 관리
                  </S.DevButton>
                </S.DevToolsContainer>
              )}

              <S.ThemeGrid>
                {currentThemes.map((theme, index) => {
                  const state = themeStates[theme.id] || {};
                  // [수정] AppContext의 themeHighScores 사용
                  const bestScore = themeHighScores[theme.id] || 0;
                  const isSelected = selectedTheme && selectedTheme.id === theme.id;
                  // [LCP 최적화] 첫 번째 카드만 LCP 후보로 고정, 두 번째는 UX만 빠르게
                  const isFirstCard = index === 0;
                  const isSecondCard = index === 1;

                  return (
                    <ThemeCardWithDownload
                      key={theme.id}
                      theme={{ ...theme, bestScore }}
                      state={state}
                      onClick={handleThemeClick}
                      themeConfig={themeConfig}
                      afterFirstPaint={afterFirstPaint}
                      isThemeDownloaded={isThemeDownloaded}
                      isSelected={isSelected}  // ✅ 선택 상태 추가
                      isFirstCard={isFirstCard}  // ✅ 첫 번째 카드 식별
                      isSecondCard={isSecondCard}  // ✅ 두 번째 카드 식별
                    />
                  );
                })}
              </S.ThemeGrid>

              <ThemePagination
                totalPages={totalPages}
                currentPage={currentThemePage}
                onPageChange={handlePageChange}
              />

              {/* 게임 모드 섹션 */}
              <S.GameModeSection>
                {/* 🔧 테마 플레이 버튼 - 왼쪽으로 이동 (주요 액션) */}
                <S.ThemePlayButton
                  onClick={handleThemePlay}
                  disabled={!selectedTheme}
                >
                  <span>
                    <span className="play-icon">▶</span>
                    <span className="play-text">{t('theme_play_button')}</span>
                  </span>
                  <span className="desc">
                    {selectedTheme ? t(selectedTheme.nameKey) : t('select_theme')}
                  </span>
                </S.ThemePlayButton>

                {/* 🔧 랜덤 플레이 버튼 - 오른쪽으로 이동 (보조 액션) */}
                <S.RandomPlayButton onClick={handleRandomPlay}>
                  <span>🎲 {t('random_play')}</span>
                  <span className="desc">{t('random_play_desc')}</span>
                </S.RandomPlayButton>
              </S.GameModeSection>

              {/* [Deleted] EventModeContainer (Season Ranking, NFT) */}
              {/* [Deleted] ButtonRow (AdFree, Donation) -> Moved to Shop/Apps */}
              {/* [Deleted] HallOfFame -> Moved to Apps */}

            </S.ThemeSection>
          </>
        )}
        </Suspense>

        <S.BottomBar>
          {/* 1. MY */}
          <S.MyButton
            onClick={() => setViewMode('my')}
            $isActive={viewMode === 'my'}
          >
            <IoPersonOutline size={22} color={viewMode === 'my' ? '#FFD700' : '#94A3B8'} />
            <S.BottomNavLabel style={{ color: viewMode === 'my' ? '#FFD700' : '#94A3B8' }}>MY</S.BottomNavLabel>
          </S.MyButton>

          {/* 2. DEAL */}
          <S.MenuButton
            onClick={() => setViewMode('deal')}
            $isActive={viewMode === 'deal'}
          >
            <IoPricetagOutline size={22} style={{ color: viewMode === 'deal' ? '#FFD700' : '#94A3B8' }} />
            <S.BottomNavLabel style={{ color: viewMode === 'deal' ? '#FFD700' : '#94A3B8' }}>DEAL</S.BottomNavLabel>
          </S.MenuButton>

          {/* 3. SPOT (Main/Center) */}
          <S.SpotButton
            onClick={() => setViewMode('spot')}
            $isMain={true}
            $isActive={viewMode === 'spot'}
          >
            <S.SpotIconWrapper>
              <IoLocationOutline size={26} />
            </S.SpotIconWrapper>
            <S.BottomNavLabel>SPOT</S.BottomNavLabel>
          </S.SpotButton>

          {/* 4. APPS */}
          <S.AppsButton
            onClick={() => setViewMode('apps')}
            $isActive={viewMode === 'apps'}
          >
            <IoGridOutline size={22} style={{ color: viewMode === 'apps' ? '#FFD700' : '#94A3B8' }} />
            <S.BottomNavLabel style={{ color: viewMode === 'apps' ? '#FFD700' : '#94A3B8' }}>APPS</S.BottomNavLabel>
          </S.AppsButton>

          {/* 5. SHOP - 번개 아이콘 */}
          <S.ShopButton
            onClick={() => setViewMode('shop')}
            $isActive={viewMode === 'shop'}
          >
            <IoFlashOutline size={22} color={viewMode === 'shop' ? '#FFD700' : '#94A3B8'} />
            <S.BottomNavLabel style={{ color: viewMode === 'shop' ? '#FFD700' : '#94A3B8' }}>SHOP</S.BottomNavLabel>
          </S.ShopButton>
        </S.BottomBar>

        {/* 🎯 [핵심 수정] 프리로딩 상태 제거 - 결제 처리 로딩만 표시 */}
        {isProcessingPayment && (
          <S.LoadingOverlay>
            <div className="spinner"></div>
            <div className="loading-text">
              {t('processing_payment')}
            </div>
          </S.LoadingOverlay>
        )}
      </S.MainContainer >

      {/* 광고 제거 옵션 모달 */}
      {/* [Removed] AdFreeModal */}

      {/* [추가] 후원 모달 */}
      {/* [Removed] DonationModal */}

      {/* 🎯 [추가] 햄버거 메뉴 모달 */}
      {
        showHamburgerMenu && (
          <Suspense fallback={<div style={{ height: '300px', background: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '15px' }}>Loading...</div>}>
            <HamburgerMenu
              onClose={() => setShowHamburgerMenu(false)}
            />
          </Suspense>
        )
      }
      {/* 랭킹 모달 — 열 때만 마운트·청크 로드 (닫힌 상태에서 Ranking 훅/렌더 비용 제거) */}
      {showRankingModal && (
        <Suspense fallback={<div style={{ height: '500px', background: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '15px' }}>Loading...</div>}>
          <Ranking
            show
            onClose={() => setShowRankingModal(false)}
            activeTab={rankingActiveTab}
          />
        </Suspense>
      )}

      {/* 시즌 랭킹 모달 추가 */}
      {/* [Removed] SeasonRankingModal */}

      {/* ShopModal removed */}

      {showMiniGameModal && (
        <Suspense fallback={null}>
          <MiniGameModal
            show={showMiniGameModal}
            onClose={() => setShowMiniGameModal(false)}
          />
        </Suspense>
      )}

      {/* [NEW] Attendance Modal — Apps에서 열 때만 청크를 로드 */}
      {showAttendanceModal && (
        <Suspense fallback={null}>
          <AttendanceModal
            isOpen={showAttendanceModal}
            onClose={closeAttendance}
            userId={piUser?.uid}
            environment={environment}
            initialData={attendanceData}
          />
        </Suspense>
      )}


      {/* 공지사항 모달 */}
      {/* [Removed] NoticeModal */}

      {/* [신규 추가] 시스템 도구 모달들 */}
      {showPasswordModal && <Suspense fallback={<div style={{ height: '200px', background: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '15px' }}>Loading...</div>}>
        <AdminPasswordModal
          show={showPasswordModal}
          onSubmit={handleAuthSubmit}
          onCancel={handleAuthCancel}
          isValidating={isValidatingAuth}
        />
      </Suspense>}

      {usagePanelMounted && <Suspense fallback={<div style={{ height: '600px', background: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '15px' }}>Loading...</div>}>
        <UsageStatsPanel
          show={showUsagePanel}
          onClose={() => setShowUsagePanel(false)}
          adminPassword={systemAuthValue}
          userId={piUser?.uid || sessionStorage.getItem('pi-user-id')}
        />
      </Suspense>}

      {maintenancePanelMounted && <Suspense fallback={<div style={{ height: '500px', background: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '15px' }}>Loading...</div>}>
        <MaintenancePanel
          show={showMaintenancePanel}
          onClose={() => setShowMaintenancePanel(false)}
          adminPassword={systemAuthValue}
          userId={piUser?.uid || sessionStorage.getItem('pi-user-id')}
        />
      </Suspense>}

      {/* 🔧 [신규] 익명 사용자 관리 모달 */}
      {
        showAnonymousUserManager && (
          <S.ModalOverlay>
            <S.ModalContent>
              <S.ModalHeader>
                <h3>👤 익명 사용자 관리</h3>
                <button onClick={() => setShowAnonymousUserManager(false)}>×</button>
              </S.ModalHeader>
              <S.ModalBody>
                {anonymousUserStats ? (
                  <div>
                    <h4>📊 사용자 통계</h4>
                    <p><strong>전체 사용자:</strong> {anonymousUserStats.totalUsers.toLocaleString()}명</p>
                    <p><strong>익명 사용자:</strong>
                      <span style={{
                        color: anonymousUserStats.anonymousUsers > 0 ? '#e74c3c' : '#27ae60',
                        fontWeight: 'bold'
                      }}>
                        {anonymousUserStats.anonymousUsers.toLocaleString()}명
                      </span>
                      {anonymousUserStats.anonymousUsers === 0 && ' ✅'}
                    </p>
                    <p><strong>일반 사용자:</strong>
                      <span style={{
                        color: anonymousUserStats.regularUsers > 0 ? '#27ae60' : '#e74c3c',
                        fontWeight: 'bold'
                      }}>
                        {anonymousUserStats.regularUsers.toLocaleString()}명
                      </span>
                      {anonymousUserStats.regularUsers > 0 && ' ✅'}
                    </p>
                    <p><strong>익명 비율:</strong> {anonymousUserStats.anonymousPercentage}%</p>
                    <p><strong>일반 사용자 비율:</strong> {((anonymousUserStats.regularUsers / anonymousUserStats.totalUsers) * 100).toFixed(2)}%</p>

                    <div style={{ marginTop: '20px' }}>
                      <button
                        onClick={deleteAnonymousUsers}
                        disabled={isDeletingAnonymous}
                        style={{
                          backgroundColor: '#ff4757',
                          color: 'white',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: '5px',
                          cursor: isDeletingAnonymous ? 'not-allowed' : 'pointer',
                          marginRight: '10px'
                        }}
                      >
                        {isDeletingAnonymous ? '삭제 중...' : '🚫 모든 익명 사용자 삭제'}
                      </button>
                      <button
                        onClick={fetchAnonymousUserStats}
                        style={{
                          backgroundColor: '#3742fa',
                          color: 'white',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: '5px',
                          cursor: 'pointer'
                        }}
                      >
                        🔄 통계 새로고침
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p>익명 사용자 통계를 조회하려면 아래 버튼을 클릭하세요.</p>
                    <button
                      onClick={fetchAnonymousUserStats}
                      style={{
                        backgroundColor: '#3742fa',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '5px',
                        cursor: 'pointer'
                      }}
                    >
                      📊 통계 조회
                    </button>
                  </div>
                )}
              </S.ModalBody>
            </S.ModalContent>
          </S.ModalOverlay>
        )
      }

      {/* 🔧 [신규] Test-Pi 전송 모달 */}
      {
        showTestPiSender && (
          <S.ModalOverlay>
            <S.ModalContent>
              <S.ModalHeader>
                <h3>💰 Test-Pi 전송</h3>
                <button onClick={() => setShowTestPiSender(false)}>×</button>
              </S.ModalHeader>
              <S.ModalBody>
                <Suspense fallback={<div style={{ height: '300px', background: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '15px' }}>Loading...</div>}>
                  <TestPiSender
                    userId={piUser?.uid || sessionStorage.getItem('pi-user-id')}
                  />
                </Suspense>
              </S.ModalBody>
            </S.ModalContent>
          </S.ModalOverlay>
        )
      }

      {/* ✅ [Admin] App.js의 AdminPage로 이동됨 - 더 이상 Main.js에서 렌더링하지 않음 */}
      {/* {showAdminPasswordModal && ( ... )} */}
      {/* {showAdminDashboard && ( ... )} */}

      {/* [모바일 디버깅] URL에 ?debug=<REACT_APP_LOG_ACCESS_KEY> 있을 때 광고 에러 원인 화면 표시 */}
      <AdDebugPanel info={lastAdDebugInfo} onClear={clearLastAdDebugInfo} />

      {/* [NEW] 랭킹전 로딩 오버레이 */}
      {isRankingLoading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.3)', zIndex: 11000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'none'
        }}>
          <div style={{
            background: 'rgba(0, 0, 0, 0.8)',
            padding: '24px 32px',
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <div className="spinner-border text-light" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div style={{ marginTop: '16px', color: 'white', fontWeight: '600', fontSize: '1rem' }}>
              {t('ranking_entering') || '랭킹전 입장 중...'}
            </div>
          </div>
        </div>
      )}

    </SpotProvider>
  );
};

export default React.memo(Main);
